/**
 * One headline tile of the case dashboard: the count, how many of it came in the last 30 days,
 * and a 12-month sparkline (one series, so no legend: the tile's name says what it counts).
 */
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { type DashboardTileView, formatCount, formatMonth } from './dashboard-view'

const SPARK_WIDTH = 120
const SPARK_HEIGHT = 32
/** Room for the 2px line and its end marker at the edges. */
const SPARK_PAD = 4

function sparkPoints(counts: readonly number[]): [number, number][] {
  const peak = Math.max(1, ...counts)
  const step = counts.length > 1 ? (SPARK_WIDTH - 2 * SPARK_PAD) / (counts.length - 1) : 0
  return counts.map((count, i) => [
    SPARK_PAD + i * step,
    SPARK_HEIGHT - SPARK_PAD - (count / peak) * (SPARK_HEIGHT - 2 * SPARK_PAD),
  ])
}

function Sparkline({ tile, label }: { tile: DashboardTileView; label: string }) {
  const { i18n } = useTranslation()
  const points = sparkPoints(tile.sparkline.map(point => point.count))
  const last = points[points.length - 1]
  return (
    <svg
      viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
      className="h-8 w-30 shrink-0 overflow-visible"
      role="img"
      aria-label={label}
    >
      <line
        x1={SPARK_PAD}
        x2={SPARK_WIDTH - SPARK_PAD}
        y1={SPARK_HEIGHT - SPARK_PAD}
        y2={SPARK_HEIGHT - SPARK_PAD}
        className="stroke-border"
        strokeWidth={1}
      />
      <polyline
        points={points.map(([x, y]) => `${x},${y}`).join(' ')}
        fill="none"
        className="stroke-primary"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {tile.sparkline.map((point, i) => (
        // A hit target wider than the line, one column per month, with the month's count.
        <rect
          key={point.month}
          x={points[i][0] - SPARK_WIDTH / tile.sparkline.length / 2}
          y={0}
          width={SPARK_WIDTH / tile.sparkline.length}
          height={SPARK_HEIGHT}
          className="fill-transparent"
        >
          <title>{`${formatMonth(point.month, i18n.language)}: ${formatCount(point.count, i18n.language)}`}</title>
        </rect>
      ))}
      {last && <circle cx={last[0]} cy={last[1]} r={3} className="fill-primary stroke-card" />}
    </svg>
  )
}

export function DashboardTile({ tile }: { tile: DashboardTileView }) {
  const { t, i18n } = useTranslation()
  const name = t(`caseDashboard.tiles.${tile.key}`)
  return (
    <Card className="flex flex-col gap-3 rounded-xl p-4">
      <h3 className="text-sm font-medium text-muted-foreground">{name}</h3>
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="text-3xl font-semibold leading-none tracking-tight text-foreground">
            {formatCount(tile.total, i18n.language)}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {t('caseDashboard.tiles.last30Days', {
              value: formatCount(tile.last30Days, i18n.language),
            })}
          </p>
        </div>
        <Sparkline tile={tile} label={t('caseDashboard.tiles.sparkline', { name })} />
      </div>
    </Card>
  )
}
